import { z } from 'zod/v3';
import {
  errorMap,
  zDescription,
  zIsoDate,
  zValidPeriod,
} from '../../common/utils';
import { DataVersionStatusSchema } from '../../input-data';
import translations from './OutputDataSchema.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ChangedBySchema } from '../../user';
import { zGroupDescriptionRef } from '../../common/utils/schema/zGroupDescriptionRef';
import { parseGroupDescriptionRef } from '../../dataobject/valueDate/utils';
import { TimeSeriesOutputType } from './types';
import type { SystemClassifierItemDto } from '../../systemClassifier';

const { addTranslations, t } = i18nNsFactory('output-data-schema');
addTranslations(translations);

export const OutputDescriptionProcessingStateSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  outputTypeId: z.number(),
  frequencyId: z.number(),
  activeDataObjectVersionId: z.number().nullable(),
  activeDataObjectDescriptionVersionId: z.number().nullable(),
  activeDataObjectVersionNumber: z.string().nullable(),
  activeDataObjectVersionStatus: DataVersionStatusSchema.nullable(),
  outputDataObjectId: z.number().nullable(),
  processingGroupId: z.number().nullable(),
  processingStateCode: z.string().nullable(),
  processingTypeId: z.number().int().nullable(),
  processingMethodId: z.number().int().nullable(),
  dataProcessingScopeId: z.number().int().nullable(),
  processingTime: z.string().nullable(),
  processingStartedById: z.number().int().nullable(),
  processingStartedByUsername: z.string().nullable(),
  activeDataObjectVersionIsMissingSourceData: z.boolean().nullable(),
  activeDataObjectVersionValidationErrorLevelId: z.number().int().nullable(),
  activeDataObjectCalculationErrors: z.boolean().nullable(),
  latestProcessingEventId: z.number().nullable(),
  hasAccess: z.boolean(),
});

export const OutputDescriptionProcessingStatesSchema = z.object({
  groupId: z.number(),
  groupCode: z.string(),
  groupName: z.string(),
  valueDate: zIsoDate(),
  frequencyId: z.number(),
  outputs: z.array(OutputDescriptionProcessingStateSchema),
});

export const OutputDataConfirmationSchema = z.object({
  id: z.number(),
  dataObjectId: z.number(),
  versionId: z.number(),
  versionNumber: z.string(),
  versionStatus: DataVersionStatusSchema,
  code: z.string(),
  name: z.string(),
  comment: z.string().nullable(),
  confirmedAt: z.string(),
  confirmedBy: ChangedBySchema,
});

export const OutputDataConfirmationsSchema = z.object({
  groupId: z.number(),
  groupCode: z.string(),
  groupName: z.string(),
  valueDate: zIsoDate(),
  confirmations: z.array(OutputDataConfirmationSchema),
});

export const OutputDataConfirmationMutationSchema = z.object({
  id: z.number(),
  comment: z.string().nullable(),
});

export const OutputDataConfirmationsMutationSchema = z.object({
  groupId: z.number(),
  confirmations: z.array(OutputDataConfirmationMutationSchema),
});

export const CalculateOutputDataSchema = z.object({
  groupId: z.number(),
  sourceDataVersionId: z.number(),
  outputIds: z.array(z.number()),
  valueDate: zIsoDate(),
  validate: z.boolean(),
});

export const CancelErrorsOutputDataSchema = z.object({
  groupId: z.number(),
  outputIds: z.array(z.number()),
  valueDate: zIsoDate(),
});

export const ValidateOutputDataSchema = z.object({
  groupId: z.number(),
  outputIds: z.array(z.number()),
  valueDate: zIsoDate(),
});

export const ConfirmOutputDataSchema = z.object({
  groupId: z.number(),
  outputIds: z.array(z.number()),
  sourceDataVersionId: z.number(
    errorMap(() => t('validation.sourceDataVersionId.required')),
  ),
  statusId: z.number(errorMap(() => t('validation.statusId.required'))),
  comment: zDescription(),
  valueDate: zIsoDate(),
  change: z.boolean().default(false),
  calculate: z.boolean().default(false),
});

export const buildConfirmOutputDataSchema = (
  sourceDataVersionItems: SystemClassifierItemDto[],
  confirmationStatusItems: SystemClassifierItemDto[],
) =>
  ConfirmOutputDataSchema.superRefine((data, ctx) => {
    const sourceDataVersionCode = getCodeByItemId(
      sourceDataVersionItems,
      data.sourceDataVersionId,
    );
    const confirmationStatusItem = getItemById(
      confirmationStatusItems,
      data.statusId,
    );

    if (
      !isAllowedConfirmationStatusForSourceDataVersion(
        sourceDataVersionCode,
        confirmationStatusItem,
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation.statusId.invalidForSourceDataVersion'),
        path: ['statusId'],
      });
    }
  });

export const DeleteOutputDataSchema = z.object({
  groupId: z.number(),
  versionId: z.number(),
});

export const TimeSeriesOutputSchema = z.object({
  groupDescriptionRef: zGroupDescriptionRef,
  datePeriod: zValidPeriod()
    .nullable()
    .refine(
      (val) => val !== null && !!val[0] && !!val[1],
      () => ({
        message: t('validation.datePeriod.required'),
      }),
    ),
});

export const CalculateTimeSeriesOutputSchema = TimeSeriesOutputSchema.extend({
  sourceDataVersionId: z.number(
    errorMap(() => t('validation.sourceDataVersionId.required')),
  ),
});

export const CalculateTimeSeriesSchema = z.object({
  outputs: z.array(CalculateTimeSeriesOutputSchema),
  validate: z.boolean(),
});

export const CalculateTimeSeriesMutationSchema =
  CalculateTimeSeriesSchema.transform(({ outputs, validate }) => ({
    outputs: groupTimeSeriesOutputs(outputs),
    validate,
  }));

export const ValidateTimeSeriesSchema = z.object({
  outputs: z.array(TimeSeriesOutputSchema),
});

export const ValidateTimeSeriesMutationSchema =
  ValidateTimeSeriesSchema.transform(({ outputs }) => ({
    outputs: groupTimeSeriesOutputs(outputs),
  }));

export const ConfirmTimeSeriesOutputSchema = TimeSeriesOutputSchema.extend({
  sourceDataVersionId: z.number(
    errorMap(() => t('validation.sourceDataVersionId.required')),
  ),
  statusId: z.number(errorMap(() => t('validation.statusId.required'))),
  comment: zDescription(),
});

export const ConfirmTimeSeriesSchema = z.object({
  outputs: z.array(ConfirmTimeSeriesOutputSchema),
  change: z.boolean().default(false),
  calculate: z.boolean().default(false),
});

export const ConfirmTimeSeriesMutationSchema =
  ConfirmTimeSeriesSchema.transform(({ outputs, change, calculate }) => ({
    outputs: groupTimeSeriesOutputs(outputs),
    change,
    calculate,
  }));

export const buildConfirmTimeSeriesSchema = (
  sourceDataVersionItems: SystemClassifierItemDto[],
  confirmationStatusItems: SystemClassifierItemDto[],
  fixedSourceDataVersionId?: number,
) =>
  z
    .preprocess((input) => {
      if (fixedSourceDataVersionId === undefined) return input;
      if (!input || typeof input !== 'object') return input;

      const data = input as { outputs?: Array<Record<string, unknown>> };
      if (!Array.isArray(data.outputs)) return input;

      return {
        ...data,
        outputs: data.outputs.map((output) => ({
          ...output,
          sourceDataVersionId: fixedSourceDataVersionId,
        })),
      };
    }, ConfirmTimeSeriesSchema)
    .superRefine(({ outputs }, ctx) => {
      outputs.forEach((output, index) => {
        const sourceDataVersionCode = getCodeByItemId(
          sourceDataVersionItems,
          output.sourceDataVersionId,
        );
        const confirmationStatusItem = getItemById(
          confirmationStatusItems,
          output.statusId,
        );

        if (
          !isAllowedConfirmationStatusForSourceDataVersion(
            sourceDataVersionCode,
            confirmationStatusItem,
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation.statusId.invalidForSourceDataVersion'),
            path: ['outputs', index, 'statusId'],
          });
        }
      });
    });

type TimeSeriesGroupOutputs<TOutput extends TimeSeriesOutputType> = {
  groupId: number;
  outputIds: number[];
  valueDateFrom: string;
  valueDateTo: string;
} & Omit<TOutput, 'groupDescriptionRef' | 'datePeriod'>;

type TimeSeriesGroupOutputsMapEntry<TOutput extends TimeSeriesOutputType> =
  TimeSeriesGroupOutputs<TOutput> & {
    hasGroupSelection: boolean;
  };

// Groups by groupId, group-level selection wins over per-description selections for the same group
export const groupTimeSeriesOutputs = <TOutput extends TimeSeriesOutputType>(
  outputs: TOutput[],
) => {
  const grouped = outputs.reduce((acc, output) => {
    const { groupDescriptionRef, datePeriod, ...groupingFields } = output;
    if (!groupDescriptionRef) return acc;

    const { groupId, descriptionId } =
      parseGroupDescriptionRef(groupDescriptionRef);

    const [valueDateFrom, valueDateTo] = datePeriod ?? [];
    if (!valueDateFrom || !valueDateTo) return acc;

    const key = `${groupId}-${valueDateFrom || '0'}-${valueDateTo || '0'}`;
    const existing = acc.get(key);
    const base = existing ?? {
      groupId,
      ...groupingFields,
      valueDateFrom,
      valueDateTo,
      outputIds: [],
      hasGroupSelection: false,
    };

    let next = base;

    if (!descriptionId) {
      // Group-level selection, it wins over description-level selections
      next = {
        ...base,
        ...groupingFields,
        valueDateFrom,
        valueDateTo,
        outputIds: [],
        hasGroupSelection: true,
      };
    } else if (!base.hasGroupSelection) {
      // Description-level selection, only applies if there is no group-level selection for the same group
      next = {
        ...base,
        outputIds: [...base.outputIds, descriptionId],
      };
    }

    acc.set(key, next);
    return acc;
  }, new Map<string, TimeSeriesGroupOutputsMapEntry<TOutput>>());

  return Array.from(grouped.values()).map(
    ({ hasGroupSelection, ...group }) => ({
      ...group,
      outputIds: hasGroupSelection
        ? []
        : Array.from(new Set(group.outputIds)).toSorted((a, b) => a - b),
    }),
  );
};

const getCodeByItemId = (items: SystemClassifierItemDto[], id: number) =>
  items.find((item) => item.id === id)?.code;

const getItemById = (items: SystemClassifierItemDto[], id: number) =>
  items.find((item) => item.id === id);

const getAllowedSourceDataVersions = (
  confirmationStatusItem: SystemClassifierItemDto | undefined,
) => {
  if (!confirmationStatusItem?.parameters) {
    return [];
  }

  try {
    const parameters = JSON.parse(confirmationStatusItem.parameters) as {
      allowedSourceDataVersions?: unknown;
    };
    return Array.isArray(parameters.allowedSourceDataVersions)
      ? parameters.allowedSourceDataVersions.filter(
          (version): version is string => typeof version === 'string',
        )
      : [];
  } catch {
    return [];
  }
};

const isAllowedConfirmationStatusForSourceDataVersion = (
  sourceDataVersionCode: string | undefined,
  confirmationStatusItem: SystemClassifierItemDto | undefined,
) => {
  if (!sourceDataVersionCode) {
    return false;
  }

  const allowedSourceDataVersions = getAllowedSourceDataVersions(
    confirmationStatusItem,
  );

  return allowedSourceDataVersions.includes(sourceDataVersionCode);
};
